<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">API Keys</h5>
        <p class="page-sub">Manage API keys that grant programmatic access to your organisation's data</p>
      </div>
      <button class="btn-primary-action" @click="openNew">
        <font-awesome-icon icon="plus" /> New API Key
      </button>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="sk-table">
      <div v-for="n in 4" :key="n" class="sk-row"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="!keys.length" class="empty-state">
      <font-awesome-icon icon="key" class="empty-icon" />
      <p>No API keys yet.</p>
      <p class="empty-sub">Create a key to start integrating external systems with this organisation.</p>
    </div>

    <!-- Key cards -->
    <div v-else class="key-list">
      <div v-for="k in keys" :key="k.id" class="key-card" :class="{ 'key-card--inactive': !k.is_active }">
        <div class="key-card-top">
          <div class="key-left">
            <div class="key-name">{{ k.name }}</div>
            <div class="key-meta">
              <code class="key-prefix">{{ k.key_prefix }}…</code>
              <span v-for="s in (k.scopes || ['*'])" :key="s" class="scope-chip" :class="scopeClass(s)">
                {{ scopeLabel(s) }}
              </span>
              <span v-if="!k.is_active" class="status-chip status-chip--red">Revoked</span>
              <span v-if="k.expires_at && isExpired(k.expires_at)" class="status-chip status-chip--red">Expired</span>
              <span v-else-if="k.expires_at" class="status-chip status-chip--amber">Expires {{ fmtDate(k.expires_at) }}</span>
            </div>
            <div v-if="k.description" class="key-desc">{{ k.description }}</div>
          </div>
          <div class="key-actions">
            <button v-if="k.can_reveal" class="btn-ghost btn-sm" @click="openReveal(k)"
              :disabled="isRevealLocked(k)" :title="isRevealLocked(k) ? 'Locked — too many failed attempts' : 'View full key'">
              <font-awesome-icon icon="eye" /> View Key
            </button>
            <button class="btn-ghost btn-sm" @click="openEdit(k)">
              <font-awesome-icon icon="edit" /> Edit
            </button>
            <button v-if="k.is_active" class="btn-ghost btn-ghost--red btn-sm" @click="revokeKey(k)">
              <font-awesome-icon icon="ban" /> Revoke
            </button>
            <button v-else class="btn-ghost btn-ghost--green btn-sm" @click="restoreKey(k)">
              <font-awesome-icon icon="check-circle" /> Restore
            </button>
            <button class="btn-ghost btn-sm" @click="openLogs(k)" title="Activity log">
              <font-awesome-icon icon="list" />
            </button>
            <button class="btn-ghost btn-ghost--red btn-sm" @click="deleteKey(k)" title="Delete key">
              <font-awesome-icon icon="trash" />
            </button>
          </div>
        </div>
        <div class="key-footer">
          <span class="key-stat">Last used: {{ k.last_used_at ? fmtDatetime(k.last_used_at) : 'Never' }}</span>
          <span class="key-stat">Created {{ fmtDate(k.created_at) }}</span>
          <span v-if="k.allowed_ips && k.allowed_ips.length" class="key-stat">
            <font-awesome-icon icon="shield-alt" style="color:#7a90b0;" /> IP restricted
          </span>
        </div>
      </div>
    </div>

    <!-- ── Create / Edit modal ───────────────────────────────────────────── -->
    <div v-if="showForm" class="modal-overlay" @click.self="showForm = false">
      <div class="modal-box">
        <div class="modal-head">
          <h5 class="modal-title">{{ editKey ? 'Edit API Key' : 'New API Key' }}</h5>
          <button class="modal-close" @click="showForm = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label class="form-label">Name <span class="req">*</span></label>
            <input v-model="form.name" class="form-input" placeholder="e.g. HR System Integration" />
          </div>
          <div class="form-row">
            <label class="form-label">Description</label>
            <input v-model="form.description" class="form-input" placeholder="Optional — what is this key used for?" />
          </div>
          <div class="form-row">
            <label class="form-label">Scopes <span class="req">*</span></label>
            <div class="scope-groups">
              <div v-for="group in scopeGroups" :key="group.module" class="scope-group">
                <div class="scope-group-label">{{ group.module }}</div>
                <div class="scope-group-items">
                  <label v-for="s in group.scopes" :key="s.value" class="scope-check">
                    <input type="checkbox" :value="s.value" v-model="form.scopes" />
                    <span class="scope-check-label">{{ s.label }}</span>
                    <span class="scope-check-hint">{{ s.hint }}</span>
                  </label>
                </div>
              </div>
            </div>
            <p class="field-hint">Select <strong>*</strong> for full access, or individual module scopes for least-privilege.</p>
          </div>
          <div class="form-row">
            <label class="form-label">Expiry Date</label>
            <input v-model="form.expires_at" type="date" class="form-input" />
          </div>
          <div class="form-row">
            <label class="form-label">IP Allowlist</label>
            <input v-model="form.allowed_ips_raw" class="form-input"
              placeholder="Comma-separated, e.g. 203.0.113.0/24, 10.0.0.1 (leave blank for any IP)" />
            <p class="field-hint">Supports individual IPs and CIDR ranges. Leave blank to allow all.</p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="showForm = false">Cancel</button>
          <button class="btn-primary-action" @click="saveKey" :disabled="saving">
            <font-awesome-icon v-if="saving" icon="spinner" spin />
            {{ saving ? 'Saving…' : (editKey ? 'Save Changes' : 'Create Key') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── New key success modal (raw key shown once) ────────────────────── -->
    <div v-if="newKeyRaw" class="modal-overlay">
      <div class="modal-box" style="max-width:520px;">
        <div class="modal-head">
          <h5 class="modal-title">API Key Created</h5>
        </div>
        <div class="modal-body">
          <div class="alert-green">
            <font-awesome-icon icon="check-circle" /> Key created successfully.
          </div>
          <p class="reveal-warning">Copy this key now — it will not be shown again in full. You can reveal it later using your account password.</p>
          <div class="key-reveal-box">
            <code>{{ newKeyRaw }}</code>
            <button class="copy-btn" @click="copyKey(newKeyRaw)">
              <font-awesome-icon :icon="copied ? 'check' : 'copy'" />
              {{ copied ? 'Copied!' : 'Copy' }}
            </button>
          </div>
          <div class="key-usage-box">
            <div class="key-usage-title">Usage</div>
            <pre class="key-usage-code">Authorization: Bearer {{ newKeyRaw }}
# or
X-Api-Key: {{ newKeyRaw }}</pre>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-primary-action" @click="newKeyRaw = null; copied = false">I've copied the key</button>
        </div>
      </div>
    </div>

    <!-- ── Reveal modal ──────────────────────────────────────────────────── -->
    <div v-if="revealTarget" class="modal-overlay" @click.self="closeReveal">
      <div class="modal-box" style="max-width:440px;">
        <div class="modal-head">
          <h5 class="modal-title">Reveal API Key</h5>
          <button class="modal-close" @click="closeReveal"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div v-if="!revealedKey">
            <p style="font-size:13px;color:#4a5e78;margin:0 0 14px;">Enter your account password to reveal the full API key for <strong>{{ revealTarget.name }}</strong>.</p>
            <div class="form-row">
              <label class="form-label">Password</label>
              <input v-model="revealPassword" type="password" class="form-input" @keyup.enter="doReveal" />
            </div>
          </div>
          <div v-else>
            <div class="key-reveal-box">
              <code>{{ revealedKey }}</code>
              <button class="copy-btn" @click="copyKey(revealedKey)">
                <font-awesome-icon :icon="copied ? 'check' : 'copy'" />
                {{ copied ? 'Copied!' : 'Copy' }}
              </button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="closeReveal">{{ revealedKey ? 'Done' : 'Cancel' }}</button>
          <button v-if="!revealedKey" class="btn-primary-action" @click="doReveal" :disabled="revealing">
            <font-awesome-icon v-if="revealing" icon="spinner" spin />
            {{ revealing ? 'Verifying…' : 'Reveal Key' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Confirm dialog (revoke / delete) ────────────────────────────── -->
    <div v-if="confirm.show" class="modal-overlay" @click.self="closeConfirm">
      <div class="modal-box confirm-box">
        <div class="confirm-icon-ring" :class="'confirm-icon-ring--' + confirm.variant">
          <font-awesome-icon :icon="confirm.icon" />
        </div>
        <div class="confirm-title">{{ confirm.title }}</div>
        <p class="confirm-body">{{ confirm.body }}</p>
        <div class="confirm-key-badge">{{ confirm.keyName }}</div>
        <div class="confirm-footer">
          <button class="btn-ghost confirm-cancel-btn" @click="closeConfirm" :disabled="confirm.loading">
            Cancel
          </button>
          <button class="btn-danger" :class="'btn-danger--' + confirm.variant" @click="runConfirm" :disabled="confirm.loading">
            <font-awesome-icon v-if="confirm.loading" icon="spinner" spin />
            {{ confirm.loading ? confirm.loadingLabel : confirm.confirmLabel }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Activity log drawer ───────────────────────────────────────────── -->
    <div v-if="logsTarget" class="modal-overlay" @click.self="logsTarget = null">
      <div class="modal-box" style="max-width:680px;">
        <div class="modal-head">
          <h5 class="modal-title">Activity Log — {{ logsTarget.name }}</h5>
          <button class="modal-close" @click="logsTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body" style="max-height:65vh;overflow-y:auto;">
          <div v-if="logsLoading" style="text-align:center;padding:30px;color:#aab8cc;">
            <font-awesome-icon icon="spinner" spin /> Loading…
          </div>
          <div v-else-if="!keyLogs.length" style="text-align:center;padding:30px;font-size:13px;color:#aab8cc;">
            No activity recorded yet.
          </div>
          <table v-else class="log-table">
            <thead>
              <tr>
                <th>Method</th><th>Endpoint</th><th>Status</th><th>IP</th><th>Time</th><th>Latency</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="l in keyLogs" :key="l.created_at + l.endpoint">
                <td><span class="method-chip" :class="'method-' + l.method.toLowerCase()">{{ l.method }}</span></td>
                <td class="ep-cell">{{ l.endpoint }}</td>
                <td><span class="status-chip" :class="l.status_code < 400 ? 'status-chip--green' : 'status-chip--red'">{{ l.status_code }}</span></td>
                <td class="mono">{{ l.ip_address || '—' }}</td>
                <td class="mono" style="white-space:nowrap;">{{ fmtDatetime(l.created_at) }}</td>
                <td class="mono">{{ l.response_ms != null ? l.response_ms + 'ms' : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

const SCOPE_GROUPS = [
  {
    module: 'Full Access',
    scopes: [
      { value: '*', label: 'Full Access (*)', hint: 'All modules, read & write' },
    ],
  },
  {
    module: 'Visitors',
    scopes: [
      { value: 'visitors:read',  label: 'Read',  hint: 'List & view visitor records'   },
      { value: 'visitors:write', label: 'Write', hint: 'Check in/out, create visits'   },
    ],
  },
  {
    module: 'HR & People',
    scopes: [
      { value: 'hr:read',  label: 'Read',  hint: 'Employees, shifts, rotas, leave'    },
      { value: 'hr:write', label: 'Write', hint: 'Create/update HR records'           },
    ],
  },
  {
    module: 'Payroll',
    scopes: [
      { value: 'payroll:read',  label: 'Read',  hint: 'Pay runs, payslips, grades'    },
      { value: 'payroll:write', label: 'Write', hint: 'Create/process payroll'        },
    ],
  },
  {
    module: 'Training',
    scopes: [
      { value: 'training:read',  label: 'Read',  hint: 'Courses, enrolments'          },
      { value: 'training:write', label: 'Write', hint: 'Enrol employees, update progress' },
    ],
  },
  {
    module: 'Exit Management',
    scopes: [
      { value: 'exits:read',  label: 'Read',  hint: 'Exit records & reports'          },
      { value: 'exits:write', label: 'Write', hint: 'Create/update exit records'      },
    ],
  },
  {
    module: 'Performance (PMS)',
    scopes: [
      { value: 'pms:read',  label: 'Read',  hint: 'Cycles, goals, reviews'            },
      { value: 'pms:write', label: 'Write', hint: 'Create/update performance records' },
    ],
  },
  {
    module: 'CRM',
    scopes: [
      { value: 'crm:read',  label: 'Read',  hint: 'Leads, accounts, contacts'         },
      { value: 'crm:write', label: 'Write', hint: 'Create/update CRM records'         },
    ],
  },
  {
    module: 'Inventory',
    scopes: [
      { value: 'inventory:read',  label: 'Read',  hint: 'Products, stock levels'      },
      { value: 'inventory:write', label: 'Write', hint: 'Create/update inventory'     },
    ],
  },
  {
    module: 'Procurement',
    scopes: [
      { value: 'procurement:read',  label: 'Read',  hint: 'Vendors, POs, sourcing events' },
      { value: 'procurement:write', label: 'Write', hint: 'Create/update procurement records' },
    ],
  },
  {
    module: 'Projects',
    scopes: [
      { value: 'projects:read',  label: 'Read',  hint: 'Projects, tasks, timesheets'  },
      { value: 'projects:write', label: 'Write', hint: 'Create/update project records' },
    ],
  },
  {
    module: 'Accounting & Finance',
    scopes: [
      { value: 'accounting:read',  label: 'Read',  hint: 'GL, journals, expenses, banks' },
      { value: 'accounting:write', label: 'Write', hint: 'Post transactions, manage accounts' },
    ],
  },
  {
    module: 'Invoicing & Billing',
    scopes: [
      { value: 'billing:read',  label: 'Read',  hint: 'Invoices, recurring billing'   },
      { value: 'billing:write', label: 'Write', hint: 'Create/send invoices'          },
    ],
  },
]

// Flat list used by scopeLabel() for chip display on key cards.
const ALL_SCOPES = SCOPE_GROUPS.flatMap(g => g.scopes)

export default {
  name: 'DeveloperApiKeys',
  setup() { return { toast: useToast() } },
  data() {
    return {
      keys: [], loading: true,
      showForm: false, editKey: null,
      form: { name: '', description: '', scopes: ['*'], expires_at: '', allowed_ips_raw: '' },
      saving: false,
      newKeyRaw: null, copied: false,
      revealTarget: null, revealPassword: '', revealedKey: null, revealing: false,
      logsTarget: null, keyLogs: [], logsLoading: false,
      scopeGroups: SCOPE_GROUPS,
      confirm: {
        show: false, variant: 'red', icon: 'ban',
        title: '', body: '', keyName: '',
        confirmLabel: '', loadingLabel: '', loading: false,
        _resolve: null,
      },
    }
  },
  created() { this.load() },
  methods: {
    async load() {
      this.loading = true
      try {
        const r = await axios.get('developer/api-keys')
        this.keys = r.data.data ?? []
      } catch { this.toast.error('Failed to load API keys.') }
      finally { this.loading = false }
    },
    openNew() {
      this.editKey = null
      this.form = { name: '', description: '', scopes: ['*'], expires_at: '', allowed_ips_raw: '' }
      this.showForm = true
    },
    openEdit(k) {
      this.editKey = k
      this.form = {
        name:           k.name,
        description:    k.description || '',
        scopes:         [...(k.scopes || ['*'])],
        expires_at:     k.expires_at || '',
        allowed_ips_raw: (k.allowed_ips || []).join(', '),
      }
      this.showForm = true
    },
    async saveKey() {
      if (!this.form.name.trim()) { this.toast.error('Name is required.'); return }
      if (!this.form.scopes.length) { this.toast.error('Select at least one scope.'); return }
      this.saving = true
      const payload = {
        name:        this.form.name,
        description: this.form.description,
        scopes:      this.form.scopes,
        expires_at:  this.form.expires_at || null,
        allowed_ips: this.form.allowed_ips_raw
          ? this.form.allowed_ips_raw.split(',').map(s => s.trim()).filter(Boolean)
          : [],
      }
      try {
        if (this.editKey) {
          await axios.put(`developer/api-keys/${this.editKey.id}`, payload)
          this.toast.success('API key updated.')
          this.showForm = false
          this.load()
        } else {
          const r = await axios.post('developer/api-keys', payload)
          this.newKeyRaw = r.data.data?.raw_key
          this.showForm = false
          this.load()
        }
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to save.')
      } finally { this.saving = false }
    },
    async revokeKey(k) {
      const ok = await this.openConfirm({
        variant: 'amber', icon: 'ban',
        title: 'Revoke API Key',
        body: 'All requests using this key will be rejected immediately. You can restore it later.',
        keyName: k.name,
        confirmLabel: 'Yes, Revoke',
        loadingLabel: 'Revoking…',
      })
      if (!ok) return
      try {
        await axios.post(`developer/api-keys/${k.id}/revoke`)
        this.toast.success('API key revoked.')
        this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to revoke.') }
      finally { this.closeConfirm() }
    },
    async restoreKey(k) {
      try {
        await axios.post(`developer/api-keys/${k.id}/restore`)
        this.toast.success('API key restored.')
        this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to restore.') }
    },
    async deleteKey(k) {
      const ok = await this.openConfirm({
        variant: 'red', icon: 'trash',
        title: 'Delete API Key',
        body: 'This is permanent and cannot be undone. Any integrations using this key will stop working.',
        keyName: k.name,
        confirmLabel: 'Yes, Delete',
        loadingLabel: 'Deleting…',
      })
      if (!ok) return
      try {
        await axios.delete(`developer/api-keys/${k.id}`)
        this.toast.success('API key deleted.')
        this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to delete.') }
      finally { this.closeConfirm() }
    },
    openConfirm(opts) {
      return new Promise(resolve => {
        Object.assign(this.confirm, opts, { show: true, loading: false, _resolve: resolve })
      })
    },
    runConfirm() {
      this.confirm.loading = true
      this.confirm._resolve(true)
    },
    closeConfirm() {
      if (this.confirm._resolve && !this.confirm.loading) this.confirm._resolve(false)
      Object.assign(this.confirm, { show: false, loading: false, _resolve: null })
    },
    openReveal(k) {
      this.revealTarget = k; this.revealPassword = ''; this.revealedKey = null
    },
    closeReveal() {
      this.revealTarget = null; this.revealPassword = ''; this.revealedKey = null; this.copied = false
    },
    async doReveal() {
      if (!this.revealPassword) { this.toast.error('Enter your password.'); return }
      this.revealing = true
      try {
        const r = await axios.post(`developer/api-keys/${this.revealTarget.id}/reveal`, { password: this.revealPassword })
        this.revealedKey = r.data.data?.raw_key
        this.revealPassword = ''
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Could not reveal key.')
      } finally { this.revealing = false }
    },
    async openLogs(k) {
      this.logsTarget = k; this.keyLogs = []; this.logsLoading = true
      try {
        const r = await axios.get(`developer/api-keys/${k.id}/logs`)
        this.keyLogs = r.data.data ?? []
      } catch { this.toast.error('Failed to load logs.') }
      finally { this.logsLoading = false }
    },
    copyKey(key) {
      navigator.clipboard?.writeText(key)
      this.copied = true
      setTimeout(() => { this.copied = false }, 2000)
    },
    scopeLabel(s) {
      return ALL_SCOPES.find(x => x.value === s)?.label ?? s
    },
    scopeClass(s) {
      if (s === '*') return 'scope-chip--full'
      if (s.endsWith(':write')) return 'scope-chip--write'
      return 'scope-chip--read'
    },
    isRevealLocked(k) {
      return k.reveal_locked_until && new Date(k.reveal_locked_until) > new Date()
    },
    isExpired(d) {
      return d && new Date(d) < new Date()
    },
    fmtDate(d) {
      return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
    },
    fmtDatetime(d) {
      return d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:24px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.key-list { display:flex; flex-direction:column; gap:12px; }
.key-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:16px 20px; }
.key-card--inactive { opacity:.65; background:#fafafa; }
.key-card-top { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.key-left  { flex:1; min-width:0; }
.key-name  { font-size:15px; font-weight:700; color:#1a2b45; margin-bottom:6px; }
.key-meta  { display:flex; align-items:center; gap:6px; flex-wrap:wrap; margin-bottom:4px; }
.key-prefix{ font-size:12px; background:#f0f4f8; color:#3d5068; padding:2px 8px; border-radius:6px; letter-spacing:.03em; }
.key-desc  { font-size:12px; color:#7a90b0; margin-top:4px; }
.key-actions { display:flex; gap:6px; align-items:center; flex-wrap:wrap; flex-shrink:0; }
.key-footer { display:flex; gap:16px; flex-wrap:wrap; margin-top:10px; padding-top:10px; border-top:1px solid #f0f4f8; }
.key-stat   { font-size:11px; color:#aab8cc; }

.scope-chip { display:inline-flex; align-items:center; padding:2px 8px; border-radius:20px; font-size:11px; font-weight:600; }
.scope-chip--full  { background:#eff6ff; color:#1d4ed8; }
.scope-chip--read  { background:#f0fdf4; color:#16a34a; }
.scope-chip--write { background:#fff7ed; color:#c2410c; }

.status-chip { display:inline-flex; align-items:center; padding:2px 8px; border-radius:20px; font-size:11px; font-weight:600; }
.status-chip--red   { background:#fee2e2; color:#dc2626; }
.status-chip--green { background:#dcfce7; color:#16a34a; }
.status-chip--amber { background:#fef3c7; color:#d97706; }

.scope-groups { display:flex; flex-direction:column; gap:14px; margin-top:6px; }
.scope-group  { display:flex; flex-direction:column; gap:6px; }
.scope-group-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:0 2px; }
.scope-group-items { display:grid; grid-template-columns:1fr 1fr; gap:6px; }
.scope-check { display:flex; align-items:flex-start; gap:8px; padding:8px 10px; border:1px solid #e4eaf2; border-radius:8px; cursor:pointer; }
.scope-check input { margin-top:2px; flex-shrink:0; cursor:pointer; }
.scope-check-label { font-size:13px; font-weight:600; color:#1a2b45; }
.scope-check-hint  { font-size:11px; color:#7a90b0; margin-left:auto; }
.field-hint { font-size:12px; color:#7a90b0; margin:6px 0 0; }

.alert-green { background:#dcfce7; color:#166534; border-radius:8px; padding:10px 14px; font-size:13px; font-weight:600; margin-bottom:14px; }
.reveal-warning { font-size:13px; color:#92400e; background:#fef3c7; border-radius:8px; padding:10px 14px; margin:0 0 14px; }
.key-reveal-box { display:flex; align-items:center; gap:10px; background:#f0f4f8; border-radius:10px; padding:12px 14px; margin-bottom:12px; overflow:hidden; }
.key-reveal-box code { flex:1; font-size:12px; word-break:break-all; color:#1a2b45; }
.copy-btn { flex-shrink:0; background:#004AAD; color:#fff; border:none; cursor:pointer; padding:6px 12px; border-radius:6px; font-size:12px; font-weight:600; font-family:inherit; }
.key-usage-box { background:#1a2b45; border-radius:10px; padding:14px 16px; }
.key-usage-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin-bottom:8px; }
.key-usage-code  { margin:0; font-size:12px; color:#e2e8f0; white-space:pre-wrap; word-break:break-all; font-family:monospace; }

.log-table { width:100%; border-collapse:collapse; font-size:12px; }
.log-table th { padding:8px 10px; background:#f8fafc; color:#7a90b0; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; border-bottom:1px solid #e4eaf2; text-align:left; white-space:nowrap; }
.log-table td { padding:8px 10px; border-bottom:1px solid #f0f4f8; color:#1a2b45; vertical-align:middle; }
.log-table tr:last-child td { border-bottom:none; }
.ep-cell { font-family:monospace; max-width:260px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.mono { font-family:monospace; font-size:11px; }
.method-chip { display:inline-block; padding:2px 7px; border-radius:4px; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; }
.method-get  { background:#dbeafe; color:#1d4ed8; }
.method-post { background:#dcfce7; color:#166534; }
.method-put  { background:#fef3c7; color:#92400e; }
.method-delete { background:#fee2e2; color:#dc2626; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:64px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.empty-state { text-align:center; padding:70px 20px; color:#aab8cc; }
.empty-icon  { font-size:48px; margin-bottom:14px; opacity:.5; }
.empty-state p { margin:0 0 6px; font-size:14px; }
.empty-sub   { font-size:13px !important; color:#aab8cc !important; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-ghost { display:inline-flex; align-items:center; gap:5px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:12px; font-weight:600; padding:0 12px; height:32px; border-radius:7px; cursor:pointer; font-family:inherit; white-space:nowrap; }
.btn-ghost:hover:not(:disabled) { background:#e4eaf2; }
.btn-ghost:disabled { opacity:.5; cursor:not-allowed; }
.btn-ghost--red   { color:#dc2626; }
.btn-ghost--red:hover   { background:#fff5f5; border-color:#fecaca; }
.btn-ghost--green { color:#16a34a; }
.btn-ghost--green:hover { background:#f0fdf4; border-color:#bbf7d0; }
.btn-sm { height:30px; padding:0 10px; font-size:11px; }

.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box    { background:#fff; border-radius:14px; width:100%; max-width:580px; box-shadow:0 24px 60px rgba(0,0,0,.2); max-height:90vh; display:flex; flex-direction:column; }
.modal-head   { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; flex-shrink:0; }
.modal-title  { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close  { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; padding:4px; }
.modal-body   { padding:20px 22px; display:flex; flex-direction:column; gap:14px; overflow-y:auto; flex:1; }
.modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 22px; border-top:1px solid #f0f4f8; flex-shrink:0; }
.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }
.req { color:#dc2626; }
@media(max-width:520px) { .scope-group-items { grid-template-columns:1fr; } }

/* ── Confirm dialog ──────────────────────────────────────────────────────── */
.confirm-box { max-width:400px !important; padding:32px 28px 24px; text-align:center; align-items:center; }
.confirm-icon-ring {
  width:56px; height:56px; border-radius:50%; display:flex; align-items:center; justify-content:center;
  font-size:22px; margin:0 auto 18px; flex-shrink:0;
}
.confirm-icon-ring--amber { background:#fef3c7; color:#d97706; }
.confirm-icon-ring--red   { background:#fee2e2; color:#dc2626; }
.confirm-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.confirm-body   { font-size:13px; color:#4a5e78; margin:0 0 16px; line-height:1.5; }
.confirm-key-badge {
  display:inline-block; background:#f0f4f8; color:#1a2b45; font-size:12px; font-weight:600;
  padding:5px 14px; border-radius:20px; margin-bottom:24px; max-width:100%; overflow:hidden;
  text-overflow:ellipsis; white-space:nowrap;
}
.confirm-footer { display:flex; gap:10px; justify-content:center; }
.confirm-cancel-btn { min-width:90px; justify-content:center; }
.btn-danger {
  display:inline-flex; align-items:center; justify-content:center; gap:6px;
  font-size:13px; font-weight:600; padding:0 20px; height:38px; border-radius:8px;
  border:none; cursor:pointer; font-family:inherit; min-width:110px;
}
.btn-danger:disabled { opacity:.6; cursor:not-allowed; }
.btn-danger--red   { background:#dc2626; color:#fff; }
.btn-danger--red:hover:not(:disabled)   { background:#b91c1c; }
.btn-danger--amber { background:#d97706; color:#fff; }
.btn-danger--amber:hover:not(:disabled) { background:#b45309; }
</style>
